import { Text, ErrorBoundary, Stack, VStack } from '@genial/components';
import { BoxRadius, Spacing } from '@genial/design-tokens';
import { Utils, useBreakpointValue } from '@genial/utils';
import React, { useState } from 'react';

import { TableContentItemStatus } from '../../../../components/TableContent/status';
import { useRedeemDetailsContext } from '../../contexts/redeem-details';
import { useRedeemSummaryContext } from '../../contexts/redeem-summary';

import { OperationTypeSection } from './components/OperationTypeSection';
import { ReviewSummary } from './components/ReviewSummary';
import { SentSummary } from './components/SentSummary';
import { StatusSection } from './components/StatusSection';

interface RedeemSummaryProps {}

const RedeemSummaryWrapper = ({ children }) => {
  return (
    <VStack
      mx={{ sm: Spacing['inline.xxxs'] }}
      mt={{ sm: Spacing['inline.xxxs'] }}
      p={Spacing['inline.xxs']}
      borderRadius={BoxRadius.sm}
      bgColor={Utils.ColorToken.WithOpacity('brand.tertiary.lightest', 0.3)}
      space={{ base: 0, sm: Spacing['stack.xxxs'], lg: 0 }}>
      {children}
    </VStack>
  );
};

export const RedeemSummary: React.FC<RedeemSummaryProps> = () => {
  const { basketAdvisorStatus, redeemOffersStatus } = useRedeemDetailsContext();
  const {
    redeemSummaryRefresher,
    redeemRowsSummaryRefresher,
    redeemSummaryIsError,
    isLoadingRedeemSummary,
    redeemRowsSummaryIsError,
    isLoadingRedeemRowsSummary,
  } = useRedeemSummaryContext();
  const [isOpenSummary, setIsOpenSummary] = useState(false);

  const deviceType = useBreakpointValue({
    base: 'mobile',
    sm: 'tablet',
    lg: 'desktop',
  });

  const isDesktop = deviceType === 'desktop';
  const isTablet = deviceType === 'tablet';
  const isMobile = deviceType === 'mobile';

  if (redeemOffersStatus === 'sent' && redeemSummaryIsError && !isLoadingRedeemSummary) {
    return (
      <RedeemSummaryWrapper>
        <ErrorBoundary
          onRefresh={redeemSummaryRefresher}
          errorIconOrIllustration="WarningIllustration"
          errorTitle="Houve um erro ao recuperar as informações da boleta."
          errorMessage="Não foi possível processar sua solicitação. Por favor, tente novamente mais tarde."
        />
      </RedeemSummaryWrapper>
    );
  }

  if (redeemOffersStatus === 'review' && redeemRowsSummaryIsError && !isLoadingRedeemRowsSummary) {
    return (
      <RedeemSummaryWrapper>
        <ErrorBoundary
          onRefresh={redeemRowsSummaryRefresher}
          errorIconOrIllustration="WarningIllustration"
          errorTitle="Houve um erro ao recuperar as informações da boleta."
          errorMessage="Não foi possível processar sua solicitação. Por favor, tente novamente mais tarde."
        />
      </RedeemSummaryWrapper>
    );
  }

  if (isMobile && !isOpenSummary) {
    return (
      <RedeemSummaryWrapper>
        <OperationTypeSection
          onToggle={() => setIsOpenSummary((state) => !state)}
          isOpen={isOpenSummary}
        />
      </RedeemSummaryWrapper>
    );
  }

  return (
    <RedeemSummaryWrapper>
      {isTablet && (
        <>
          <OperationTypeSection />
          <VStack w="full" h="1px" background="neutral.lightest" />
        </>
      )}
      {isMobile && (
        <OperationTypeSection
          onToggle={() => setIsOpenSummary((state) => !state)}
          isOpen={isOpenSummary}
        />
      )}
      <Stack
        direction={{ base: 'column', lg: 'row' }}
        space={Spacing['stack.nano']}
        justifyContent="space-between">
        {isDesktop && (
          <VStack space={Spacing['inset.quarck']}>
            <Text color="neutral.darkest" variant="semibold">
              Operação
            </Text>
            <OperationTypeSection />
          </VStack>
        )}
        <StatusSection
          title="Status"
          status={
            <TableContentItemStatus
              maxW={{ sm: '220px', lg: '110px' }}
              basketAdvisorStatus={basketAdvisorStatus}
            />
          }
        />
        {redeemOffersStatus === 'sent' ? <SentSummary /> : <ReviewSummary />}
      </Stack>
    </RedeemSummaryWrapper>
  );
};
